import { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Users, MapPin, Globe, Tag } from 'lucide-react';

/**
 * CompanyDetailEnrichment — Round 92o++++++++k (Feb 2026)
 *
 * Renders a compact metadata strip inside the About / Fund Overview
 * section using Massive's `/v3/reference/tickers/{ticker}` data
 * (cached in stock_cache). Each chip independently hides itself if
 * the underlying field is missing, so:
 *   - A fully-populated equity like AAPL shows all four chips
 *     (Listed since · Employees · HQ · SIC).
 *   - An ETF without an employee count still renders the
 *     "Listed since" chip if Massive has that, and so on.
 *   - A ticker Massive doesn't know (delisted, OTC) → component
 *     renders nothing at all.
 *
 * Auto-fetches on mount with a single GET; the backend handles
 * caching (12-month TTL) and the apiKey, so this is just a thin
 * presenter.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Chip({ icon: Icon, label, value, testId, href }) {
  if (value === null || value === undefined || value === '') return null;
  const body = (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground" data-testid={testId}>
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span className="opacity-70">{label}</span>
      <span className="font-bold text-foreground/80">{value}</span>
    </span>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
        {body}
      </a>
    );
  }
  return body;
}

function formatEmployees(n) {
  if (!n || !Number.isFinite(Number(n))) return null;
  const v = Number(n);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return String(v);
}

function formatListDate(iso) {
  if (!iso || typeof iso !== 'string' || iso.length < 4) return null;
  // Just the year is enough — "Listed since 1980" reads cleaner than
  // "Listed since 1980-12-12" and saves horizontal space in the strip.
  return iso.slice(0, 4);
}

function formatHQ(city, state) {
  if (!city && !state) return null;
  // Massive returns city in screaming-caps ("CUPERTINO") — titlecase
  // it for visual parity with the rest of the section while leaving
  // state codes alone (state is already uppercase by convention).
  const niceCity = city ? city.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : null;
  if (niceCity && state) return `${niceCity}, ${state}`;
  return niceCity || state;
}

function titleCase(s) {
  if (!s || typeof s !== 'string') return s;
  // Massive's SIC descriptions come back screaming-caps
  // ("ELECTRONIC COMPUTERS") — titlecase them for visual parity with
  // the rest of the About section.
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function CompanyDetailEnrichment({ ticker, sector, industry }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/stocks/${encodeURIComponent(ticker.toUpperCase())}/branding`, {
          withCredentials: true,
        });
        if (!cancelled) setData(data);
      } catch (_e) {
        // Silent fail — strip just doesn't render. Aligns with the
        // "no broken placeholder" UX bar from the icon work.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading || !data) return null;

  const employees = formatEmployees(data.total_employees);
  const listedYear = formatListDate(data.list_date);
  const hq = formatHQ(data.hq_city, data.hq_state);
  const sic = data.sic_description ? titleCase(data.sic_description) : null;
  const homepage = data.homepage_url;

  // Render nothing if all chips would be empty.
  if (!employees && !listedYear && !hq && !sic && !homepage && !sector) return null;

  return (
    <div
      className="flex items-center gap-x-3 gap-y-1 flex-wrap pt-2 mt-2 border-t border-border/40"
      data-testid="company-detail-enrichment"
    >
      <Chip icon={Calendar} label="Listed" value={listedYear} testId="cd-listed" />
      <Chip icon={Users} label="Employees" value={employees} testId="cd-employees" />
      <Chip icon={MapPin} label="HQ" value={hq} testId="cd-hq" />
      {/* Round 97a (Feb 2026) — The top-right chip strip in the
          Fund Overview card already shows Industry, so duplicating it
          here was redundant. Replaced with Sector. Also hidden when
          the cache row has sector === industry (some legacy rows
          store the SIC description in both fields) — that way the
          card never looks like it's showing the same value twice. */}
      <Chip
        icon={Tag}
        label="Sector"
        value={(sector && sector !== industry) ? sector : null}
        testId="cd-sector"
      />
      <Chip
        icon={Globe}
        label="Website"
        value={homepage ? homepage.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '') : null}
        testId="cd-homepage"
        href={homepage}
      />
    </div>
  );
}
